import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getSubmissionSummary, submitSubmissionUpdate, SubmissionSummary } from '../api/contentApi';

const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_SIZE_MB = 10;

const SubmissionUpdatePage: React.FC = () => {
  const { t } = useTranslation();
  const { lang, id } = useParams<{ lang: string; id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const currentLang = lang || 'de';

  const [summary, setSummary] = useState<SubmissionSummary | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!id || !token) {
      setLoadError(t('contact.updatePage.invalidToken'));
      setLoading(false);
      return;
    }

    getSubmissionSummary(id, token)
      .then((data) => {
        setSummary(data);
      })
      .catch((err: Error) => {
        if (err.message === 'invalidToken') {
          setLoadError(t('contact.updatePage.invalidToken'));
        } else {
          setLoadError(t('common.error'));
        }
      })
      .finally(() => setLoading(false));
  }, [id, token, t]);

  const validateFiles = (newFiles: File[]): string[] => {
    const errs: string[] = [];
    if (newFiles.length > DEFAULT_MAX_FILES)
      errs.push(t('contact.attachmentsHint', { maxFiles: DEFAULT_MAX_FILES, maxSize: DEFAULT_MAX_SIZE_MB }));
    newFiles.forEach((f) => {
      if (f.size > DEFAULT_MAX_SIZE_MB * 1024 * 1024)
        errs.push(`${f.name}: file too large (max ${DEFAULT_MAX_SIZE_MB} MB)`);
    });
    return errs;
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const merged = [...files, ...selected].slice(0, DEFAULT_MAX_FILES);
    setFiles(merged);
    setFileErrors(validateFiles(merged));
  };

  const removeFile = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    setFileErrors(validateFiles(updated));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !token) return;
    if (fileErrors.length > 0) return;
    if (!message.trim() && files.length === 0) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      await submitSubmissionUpdate(id, token, message, files);
      setSubmitted(true);
    } catch (err: any) {
      if (err.message === 'closed') {
        setSubmitError(t('contact.updatePage.closedMessage'));
      } else if (err.message === 'invalidToken') {
        setSubmitError(t('contact.updatePage.invalidToken'));
      } else {
        setSubmitError(err.message || t('contact.updatePage.errorMessage'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(currentLang === 'de' ? 'de-CH' : currentLang, {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('contact.updatePage.title')} — CLAIRMONT | Rechtsanwalt Marcel Küster</title>
        <html lang={currentLang} />
      </Helmet>

      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/update.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('contact.updatePage.title')}</h1>
        </div>
      </section>

      <div className="max-w-2xl mx-auto py-12 px-4">
        {loading && (
          <p className="text-gray-500 text-center">{t('common.loading')}</p>
        )}

        {!loading && loadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <p className="text-red-700">{loadError}</p>
          </div>
        )}

        {!loading && !loadError && summary?.isClosed && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
            <p className="text-amber-800">{t('contact.updatePage.closedMessage')}</p>
          </div>
        )}

        {!loading && !loadError && !summary?.isClosed && submitted && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-serif font-bold text-green-800 mb-4">&#10003;</h2>
            <p className="text-green-700 text-lg">{t('contact.updatePage.successMessage')}</p>
          </div>
        )}

        {!loading && !loadError && !summary?.isClosed && !submitted && (
          <>
            {summary && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-sm text-gray-600">
                <p className="font-medium text-gray-700">{summary.subject}</p>
                <p className="mt-1">
                  {t('contact.updatePage.context', { date: formatDate(summary.submittedAt) })}
                </p>
              </div>
            )}

            {submitError && (
              <div className="error mb-6">{submitError}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="updateMessage" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.message')}
                </label>
                <textarea
                  id="updateMessage"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  placeholder={t('contact.updatePage.messagePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contact.attachments')}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {t('contact.attachmentsHint', { maxFiles: DEFAULT_MAX_FILES, maxSize: DEFAULT_MAX_SIZE_MB })}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={handleFilesChange}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-sm text-gray-700 bg-gray-50 rounded px-3 py-1">
                        <span className="truncate max-w-xs">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="ml-3 text-red-500 hover:text-red-700 text-xs shrink-0"
                        >
                          {t('contact.removeFile')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {fileErrors.map((e, i) => (
                  <p key={i} className="mt-1 text-sm text-red-600">{e}</p>
                ))}
              </div>

              <button
                type="submit"
                disabled={submitting || fileErrors.length > 0 || (!message.trim() && files.length === 0)}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t('common.loading') : t('contact.updatePage.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </>
  );
};

export default SubmissionUpdatePage;
